import { useCallback, useState } from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineStack,
  Select,
  Tag,
  Text,
} from '@shopify/polaris';
import { useLanguage } from '../providers/LanguageProvider';
import { useShopify } from '../providers/AppBridgeProvider';
import {
  MAX_TARGETING_LIST_LENGTH,
  TARGETING_MODES,
  type Targeting,
  type TargetingMode,
  type TargetingRule,
} from '../types';

/**
 * `gid://shopify/Product/7712345` -> `7712345`.
 *
 * The resource picker only ever hands back GIDs; the Liquid gate only ever
 * compares against `product.id`, which is a bare number. Storing the GID
 * would make targeting by product fail in complete silence: an 'include' rule
 * would match nothing and hide the button everywhere, an 'exclude' rule would
 * match nothing and show it everywhere, with no error anywhere to say so.
 *
 * Returns null for anything that is not a product id, so a caller can refuse
 * instead of storing a value the storefront can never match. The backend runs
 * the same normalisation (routes/settings.ts) — this one exists so the
 * merchant is told at the moment of choosing, not after a save.
 */
export function normalizeProductId(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (/^[1-9][0-9]*$/.test(trimmed)) return trimmed;
  const gid = /^gid:\/\/shopify\/Product\/([1-9][0-9]*)(?:\?.*)?$/.exec(trimmed);
  return gid ? gid[1] : null;
}

/** The inverse, for handing the picker back what is already selected. */
export function toProductGid(id: string): string {
  return `gid://shopify/Product/${id}`;
}

type RuleKey = 'tryon' | 'size';

export interface TargetingSectionProps {
  targeting: Targeting;
  onChange: (targeting: Targeting) => void;
  /** ISO date of the last successful mirror into the storefront metafield. */
  syncedAt?: string | null;
  /** Why the last mirror failed, straight from Shopify. */
  syncError?: string | null;
  disabled?: boolean;
}

export function TargetingSection({
  targeting,
  onChange,
  syncedAt = null,
  syncError = null,
  disabled = false,
}: TargetingSectionProps) {
  const { t, language } = useLanguage();
  const s = t.settings;
  const { shopify } = useShopify();
  const resourcePicker = shopify?.resourcePicker;
  const pickerAvailable = typeof resourcePicker === 'function';

  // Titles are not persisted: the rules store handles and ids, which is all
  // the storefront needs. What the picker tells us in this session is kept so
  // the merchant sees names rather than numbers right after choosing.
  const [productTitles, setProductTitles] = useState<Record<string, string>>({});
  const [pickerError, setPickerError] = useState(false);

  const updateRule = useCallback(
    (key: RuleKey, rule: TargetingRule) => {
      onChange({ ...targeting, [key]: rule });
    },
    [onChange, targeting]
  );

  const pick = useCallback(
    async (key: RuleKey, kind: 'product' | 'collection') => {
      if (!resourcePicker) return;
      const rule = targeting[key];
      const chosen = kind === 'product' ? rule.productIds : rule.categories;
      const remaining = MAX_TARGETING_LIST_LENGTH - chosen.length;
      if (kind === 'collection' && remaining <= 0) return;
      setPickerError(false);
      try {
        const selection = await resourcePicker(
          kind === 'product'
            ? {
                type: 'product',
                // Preselecting what is already chosen makes the picker's
                // answer the whole intended list, so unchecking one removes
                // it. Collections cannot do this — see below.
                action: 'select',
                multiple: MAX_TARGETING_LIST_LENGTH,
                selectionIds: rule.productIds.map((id) => ({ id: toProductGid(id) })),
              }
            : {
                // Only handles are stored, so there are no GIDs to preselect
                // collections with. The picker therefore opens empty and its
                // answer is ADDED: replacing would silently drop every
                // collection chosen in an earlier visit. Removing one stays a
                // job for its tag.
                type: 'collection',
                action: 'add',
                multiple: remaining,
              }
        );
        // Cancelling resolves undefined. Reading that as "an empty selection"
        // would wipe the merchant's list without them asking.
        if (!selection) return;

        if (kind === 'product') {
          const ids: string[] = [];
          const titles: Record<string, string> = {};
          for (const item of selection) {
            const id = normalizeProductId(item.id);
            // An id we cannot normalise is one the storefront could never
            // match. Refusing the whole selection is louder than dropping it.
            if (!id) {
              setPickerError(true);
              return;
            }
            if (!ids.includes(id)) ids.push(id);
            if (item.title) titles[id] = item.title;
          }
          setProductTitles((current) => ({ ...current, ...titles }));
          updateRule(key, { ...rule, productIds: ids });
          return;
        }

        const handles = [...rule.categories];
        for (const item of selection) {
          // The storefront matches category entries against collection
          // handles. A GID here would match nothing, silently.
          const handle = item.handle?.trim();
          if (!handle) {
            setPickerError(true);
            return;
          }
          if (!handles.includes(handle)) handles.push(handle);
        }
        updateRule(key, { ...rule, categories: handles.slice(0, MAX_TARGETING_LIST_LENGTH) });
      } catch (error) {
        console.error('[Targeting] resource picker failed:', error);
        setPickerError(true);
      }
    },
    [resourcePicker, targeting, updateRule]
  );

  const coverage = (rule: TargetingRule): { text: string; warn: boolean } => {
    const collections = rule.categories.length;
    const products = rule.productIds.length;
    if (rule.mode === 'all') return { text: s.targetingCoverageAll, warn: false };
    if (rule.mode === 'include') {
      return collections + products === 0
        ? { text: s.targetingCoverageIncludeEmpty, warn: true }
        : { text: s.targetingCoverageInclude(collections, products), warn: false };
    }
    return collections + products === 0
      ? { text: s.targetingCoverageExcludeEmpty, warn: false }
      : { text: s.targetingCoverageExclude(collections, products), warn: false };
  };

  const modeOptions = TARGETING_MODES.map((mode) => ({
    value: mode,
    label:
      mode === 'all'
        ? s.targetingModeAll
        : mode === 'include'
          ? s.targetingModeInclude
          : s.targetingModeExclude,
  }));

  const renderList = (
    key: RuleKey,
    kind: 'product' | 'collection',
    entries: string[],
    labelFor: (entry: string) => string,
    onRemove: (entry: string) => void
  ) => (
    <BlockStack gap="200">
      <Text as="h4" variant="bodySm" fontWeight="semibold">
        {kind === 'collection' ? s.targetingCollections : s.targetingProducts}
      </Text>
      {entries.length === 0 ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {kind === 'collection' ? s.targetingNoCollections : s.targetingNoProducts}
        </Text>
      ) : (
        <InlineStack gap="100" wrap>
          {entries.map((entry) => (
            // The remove button's accessible name comes from the Polaris
            // locale bundle ("Remove {children}"), which main.tsx already
            // switches with the merchant's language, so there is no string of
            // ours to add here.
            <Tag key={entry} onRemove={disabled ? undefined : () => onRemove(entry)}>
              {labelFor(entry)}
            </Tag>
          ))}
        </InlineStack>
      )}
      <Box>
        <Button
          disabled={
            disabled ||
            !pickerAvailable ||
            // Collections are added to the list rather than replacing it, so
            // at the cap there is nothing the picker could do.
            (kind === 'collection' && entries.length >= MAX_TARGETING_LIST_LENGTH)
          }
          onClick={() => {
            void pick(key, kind);
          }}
        >
          {kind === 'collection' ? s.targetingChooseCollections : s.targetingChooseProducts}
        </Button>
      </Box>
      {entries.length >= MAX_TARGETING_LIST_LENGTH && (
        <Text as="p" variant="bodySm" tone="subdued">
          {s.targetingLimitReached(MAX_TARGETING_LIST_LENGTH)}
        </Text>
      )}
    </BlockStack>
  );

  const renderBlock = (key: RuleKey, title: string, description: string) => {
    const rule = targeting[key];
    const { text, warn } = coverage(rule);

    return (
      <BlockStack gap="300" data-testid={`targeting-${key}`}>
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            {title}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {description}
          </Text>
        </BlockStack>

        <Select
          label={s.targetingModeLabel}
          options={modeOptions}
          value={rule.mode}
          disabled={disabled}
          onChange={(value) => updateRule(key, { ...rule, mode: value as TargetingMode })}
        />

        {rule.mode !== 'all' && (
          <BlockStack gap="300">
            {renderList(
              key,
              'collection',
              rule.categories,
              (entry) => entry,
              (entry) =>
                updateRule(key, {
                  ...rule,
                  categories: rule.categories.filter((c) => c !== entry),
                })
            )}
            {renderList(
              key,
              'product',
              rule.productIds,
              (entry) => productTitles[entry] ?? s.targetingProductFallbackLabel(entry),
              (entry) =>
                updateRule(key, {
                  ...rule,
                  productIds: rule.productIds.filter((id) => id !== entry),
                })
            )}
            {!pickerAvailable && (
              <Text as="p" variant="bodySm" tone="subdued">
                {s.targetingPickerUnavailable}
              </Text>
            )}
          </BlockStack>
        )}

        <Text as="p" variant="bodySm" tone={warn ? 'caution' : 'subdued'} fontWeight={warn ? 'semibold' : undefined}>
          {text}
        </Text>
        {rule.mode !== 'all' && (
          <Text as="p" variant="bodySm" tone="subdued">
            {s.targetingCoverageCaveat}
          </Text>
        )}
      </BlockStack>
    );
  };

  const syncedAtDate = syncedAt ? new Date(syncedAt) : null;
  const syncedAtLabel =
    !syncError && syncedAtDate && !Number.isNaN(syncedAtDate.getTime())
      ? s.targetingSyncedAt(syncedAtDate.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US'))
      : null;

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {s.targetingTitle}
          </Text>
          <Text as="p" tone="subdued">
            {s.targetingDescription}
          </Text>
        </BlockStack>

        {/* The save succeeded and the database is right; the storefront copy
            is not. Saying nothing would leave the merchant looking at a
            product page that contradicts the settings they are reading. */}
        {syncError && (
          <Banner tone="warning" title={s.targetingSyncPendingTitle}>
            <BlockStack gap="100">
              <p>{s.targetingSyncPendingBody}</p>
              <p>{s.targetingSyncDetail(syncError)}</p>
            </BlockStack>
          </Banner>
        )}

        {pickerError && (
          <Banner tone="critical" onDismiss={() => setPickerError(false)}>
            <p>{s.targetingPickerFailed}</p>
          </Banner>
        )}

        {renderBlock('tryon', s.targetingTryonTitle, s.targetingTryonDescription)}

        <Divider />

        {renderBlock('size', s.targetingSizeTitle, s.targetingSizeDescription)}

        {syncedAtLabel && (
          <Text as="p" variant="bodySm" tone="subdued">
            {syncedAtLabel}
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}

export default TargetingSection;
